/* 범용 심플 제품 카드 캐러셀 (guide case02 / U2-v).
 *
 * 장바구니 되묻기·제품상세 되묻기 등 `.pick-list.add-cart` 심플 카드가 필요한
 * 모든 위젯의 공용 렌더러. `AddCartPickItem`(Add to Basket CTA) 한 벌.
 * 2+ 건일 때 compare 카드와 동일한 가로 스와이프(`useDragScrollbar`). */

import { useRef } from "react";
import { useDragScrollbar } from "../lib/useDragScrollbar";
import {
  AddCartPickItem,
  type AddCartCtaVariant,
  type AddCartProductFields,
} from "./pick/pickAddCartItem";

export interface SimpleProductCardData {
  products?: AddCartProductFields[];
  items?: AddCartProductFields[];
  // 되묻기 capability(제품상세 §3). feature/spec 만 summary CTA 로 분기, 그 외(cart 재확인 등)는 Add to Basket.
  // sibling(G-6) — dual CTA(Feature Summary + Add to Basket).
  capability?: string;
}

/* clarify capability → 심플 카드 CTA variant. review 는 carousel 미노출(GR-5)이라 해당 없음. */
function _ctaVariantOf(capability?: string): AddCartCtaVariant | undefined {
  if (
    capability === "feature"
    || capability === "spec"
    || capability === "select"
    || capability === "sibling"
  ) {
    return capability;
  }
  return undefined;
}

interface Props {
  data: SimpleProductCardData;
  sessionToken: string;
}

export default function SimpleProductCard({ data, sessionToken }: Props) {
  const items = data.products || data.items || [];
  const ctaVariant = _ctaVariantOf(data.capability);
  const scrollRef = useRef<HTMLDivElement>(null!);
  const barRef = useRef<HTMLDivElement>(null!);
  const trackRef = useRef<HTMLDivElement>(null!);
  const scrollbarRef = useRef<HTMLDivElement>(null!);
  useDragScrollbar(scrollRef, barRef, trackRef, scrollbarRef, [items.length]);

  // asklg_shop_product_card_list 미발화 — 장바구니 되묻기·clarify 되묻기 카드는
  // 사용자 행위로 노출된 관심 후보가 아니므로 관심 제품 생성률에서 제외(ai_top_picks만 발화).

  if (items.length === 0) {
    return <div className="ac-hidden" aria-hidden="true" />;
  }

  const multiple = items.length >= 2;
  let listClass = "pick-list add-cart";
  if (ctaVariant === "sibling") {
    listClass += " pick-list--sibling";
  }
  if (multiple) {
    listClass += " pick-list--carousel is-multiple";
  }

  return (
    <div className="ai-gp1--section">
      <div
        ref={scrollRef}
        className="ai-gp1-scroll--container"
        style={{ overscrollBehaviorX: "contain", touchAction: "pan-x" }}
      >
        <ul className={listClass}>
          {items.map((item, idx) => (
            <AddCartPickItem
              key={item.sku || idx}
              sessionToken={sessionToken}
              item={item}
              ctaVariant={ctaVariant}
              carousel={multiple}
            />
          ))}
        </ul>
      </div>
      <div ref={scrollbarRef} className="ai-gp1-custom--scrollbar hidden">
        <div ref={trackRef} className="ai-gp1-scrollbar--track">
          <div ref={barRef} className="ai-gp1-scrollbar--bar" />
        </div>
      </div>
    </div>
  );
}
